// hooks/useBids.js
// Bidding Hook - Synchronized with Mobile App
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { LIVE_POLL, LIVE_STALE_MS, livePollInterval } from "../config/liveSync";
import { normalizeMessages, normalizeOffer } from "../utils/offerNormalize";
import { invalidateNotificationQueries } from "../utils/notificationHelpers";

/** Stable empty list — avoids `data = []` default creating a new array every render. */
export const EMPTY_BID_MESSAGES = [];

const placeBidMutationOptions = (queryClient) => ({
  mutationFn: async ({ productId, bidAmount, message }) => {
    const response = await apiService.placeBid(productId, {
      bidAmount,
      amount: bidAmount,
      message,
    });
    if (response.status !== "success") {
      throw new Error(response.message || "Failed to place offer");
    }
    const payload = response.data || {};
    const offer = normalizeOffer(payload.offer || payload.bid);
    const conversation = payload.conversation;
    const conversationId =
      payload.conversationId ||
      conversation?.id ||
      conversation?._id ||
      offer?.conversation?.id ||
      offer?.conversation?._id ||
      (typeof offer?.conversation === "string" ? offer.conversation : null);
    return {
      conversationId,
      conversation,
      message: payload.message,
      offer,
      bid: offer,
    };
  },
  retry: 0,
  onSuccess: (_data, variables) => {
    queryClient.invalidateQueries({ queryKey: ["bidMessages", variables.productId] });
    queryClient.invalidateQueries({ queryKey: ["myBids"] });
    invalidateNotificationQueries(queryClient);
  },
});

const addBidToCartMutationOptions = (queryClient) => ({
  mutationFn: async ({ bidId, size, color, productId }) => {
    const bidIdStr = String(bidId || "").trim();
    if (!bidIdStr || bidIdStr.length !== 24 || !/^[0-9a-fA-F]{24}$/.test(bidIdStr)) {
      throw new Error("Invalid bid ID format. Please try again.");
    }

    let productIdStr = null;
    if (productId) {
      productIdStr = String(productId || "").trim();
      if (productIdStr.length !== 24 || !/^[0-9a-fA-F]{24}$/.test(productIdStr)) {
        productIdStr = null;
      }
    }
    const response = await apiService.addBidToCart(bidIdStr, size, color, productIdStr);
    if (response.status !== "success") {
      const errorMsg = response.message || response.error || "Failed to add bid to cart";
      throw new Error(errorMsg);
    }
    return response.data;
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ["cart"] });
    queryClient.invalidateQueries({ queryKey: ["myBids"] });
    invalidateNotificationQueries(queryClient);
  },
});

/**
 * Mutations only (no myBids query). BidChat uses this so an extra query cannot
 * trigger continuous re-renders while the messages query is polling.
 */
const acceptOfferMutationOptions = (queryClient) => ({
  mutationFn: async ({ offerId, conversationId }) => {
    const response = await apiService.acceptOffer(offerId);
    if (response.status !== "success") {
      throw new Error(response.message || "Failed to accept offer");
    }
    return response.data;
  },
  onSuccess: (_data, variables) => {
    if (variables.conversationId) {
      queryClient.invalidateQueries({ queryKey: ["conversationMessages", variables.conversationId] });
    }
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
    queryClient.invalidateQueries({ queryKey: ["myBids"] });
    invalidateNotificationQueries(queryClient);
  },
});

const rejectOfferMutationOptions = (queryClient) => ({
  mutationFn: async ({ offerId, conversationId, reason }) => {
    const response = await apiService.rejectOffer(offerId, reason);
    if (response.status !== "success") {
      throw new Error(response.message || "Failed to decline offer");
    }
    return response.data;
  },
  onSuccess: (_data, variables) => {
    if (variables.conversationId) {
      queryClient.invalidateQueries({ queryKey: ["conversationMessages", variables.conversationId] });
    }
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
    invalidateNotificationQueries(queryClient);
  },
});

const sendMessageMutationOptions = (queryClient) => ({
  mutationFn: async ({ conversationId, text, productId }) => {
    const response = await apiService.sendConversationMessage(conversationId, text);
    if (response.status !== "success") {
      throw new Error(response.message || "Failed to send message");
    }
    return response.data;
  },
  onSuccess: (_data, variables) => {
    if (variables.productId) {
      queryClient.invalidateQueries({ queryKey: ["bidMessages", variables.productId] });
    }
    queryClient.invalidateQueries({ queryKey: ["conversations"] });
    invalidateNotificationQueries(queryClient);
  },
});

export const useBidActions = () => {
  const queryClient = useQueryClient();
  const placeBidMutation = useMutation(placeBidMutationOptions(queryClient));
  const addBidToCartMutation = useMutation(addBidToCartMutationOptions(queryClient));
  const sendMessageMutation = useMutation(sendMessageMutationOptions(queryClient));
  const acceptOfferMutation = useMutation(acceptOfferMutationOptions(queryClient));
  const rejectOfferMutation = useMutation(rejectOfferMutationOptions(queryClient));

  return {
    placeBid: placeBidMutation.mutate,
    placeBidAsync: placeBidMutation.mutateAsync,
    isPlacingBid: placeBidMutation.isPending,
    placeBidError: placeBidMutation.error,
    addBidToCart: addBidToCartMutation.mutate,
    addBidToCartAsync: addBidToCartMutation.mutateAsync,
    isAddingBidToCart: addBidToCartMutation.isPending,
    sendMessage: sendMessageMutation.mutate,
    sendMessageAsync: sendMessageMutation.mutateAsync,
    isSendingMessage: sendMessageMutation.isPending,
    acceptOffer: acceptOfferMutation.mutate,
    acceptOfferAsync: acceptOfferMutation.mutateAsync,
    isAcceptingOffer: acceptOfferMutation.isPending,
    rejectOffer: rejectOfferMutation.mutate,
    rejectOfferAsync: rejectOfferMutation.mutateAsync,
    isRejectingOffer: rejectOfferMutation.isPending,
  };
};

export const useBids = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const actions = useBidActions();

  const {
    data: myBids = [],
    isLoading: isLoadingBids,
    error: bidsError,
    refetch: refetchBids,
  } = useQuery({
    queryKey: ["myBids"],
    queryFn: async () => {
      const response = await apiService.getMyBids();
      if (response.status === "success" && response.data) {
        const list = response.data.bids || response.data.offers || response.data || [];
        return Array.isArray(list) ? list.map(normalizeOffer) : [];
      }
      return [];
    },
    enabled: !!isAuthenticated,
    staleTime: LIVE_STALE_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: () => livePollInterval(LIVE_POLL.LIST),
    placeholderData: (previous) => previous,
    retry: false,
  });

  return {
    myBids,
    isLoadingBids,
    bidsError,
    ...actions,
    refetchBids,
  };
};

export const useBidMessages = (productId, options = {}) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const { enablePolling = true, pollingInterval = LIVE_POLL.CHAT } = options;

  return useQuery({
    queryKey: ["bidMessages", productId, "v3"],
    queryFn: async () => {
      try {
        const response = await apiService.getProductConversationMessages(productId);
        if (response.status === "success" && response.data) {
          return {
            messages: normalizeMessages(response.data),
            conversationId: response.conversationId || null,
          };
        }
        return { messages: EMPTY_BID_MESSAGES, conversationId: null };
      } catch (error) {
        if (error.status === 404 || error.message?.includes("Offre introuvable") || error.message?.includes("not found")) {
          return { messages: EMPTY_BID_MESSAGES, conversationId: null };
        }
        const errorMsg = (error.message || "").toLowerCase();
        if (
          errorMsg.includes("invalid token") ||
          errorMsg.includes("unauthorized") ||
          errorMsg.includes("token") ||
          errorMsg.includes("session expired") ||
          errorMsg.includes("forbidden")
        ) {
          const authError = new Error("Authentication required");
          authError.isAuthError = true;
          throw authError;
        }
        throw error;
      }
    },
    enabled: !!isAuthenticated && !!productId,
    // Loop fix: populated API graphs made React Query's deep compare blow the stack.
    structuralSharing: false,
    placeholderData: { messages: EMPTY_BID_MESSAGES, conversationId: null },
    select: (data) =>
      data && typeof data === "object" && Array.isArray(data.messages)
        ? data
        : { messages: EMPTY_BID_MESSAGES, conversationId: null },
    staleTime: 0,
    // Loop fix: use a fixed interval — dynamic refetchInterval re-evaluates query state every tick.
    refetchInterval:
      enablePolling && isAuthenticated && productId
        ? () => livePollInterval(pollingInterval)
        : false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    retryOnMount: false,
    throwOnError: (error) => !error.isAuthError,
  });
};

export default useBids;
